import { createAsyncThunk } from "@reduxjs/toolkit";

import * as wishlistsAPI from "../../../api/wishlists";
import { IWishListLine } from "../../../models/catalogue";
import { loadFavorites } from "./loadFavorites";

/**
 * Arguments for removeFromFavorites thunk.
 */
interface RemoveFromFavoritesArgs {
    lineURL: string;
}

/**
 * Remove a product from the favorites wishlist.
 *
 * This thunk removes a product from the user's favorites wishlist and reloads
 * the favorites list to reflect the change. Used on the Favorites page when
 * users want to remove items from their saved favorites.
 *
 * @param {string} args.lineURL - API URL of the wishlist line to remove
 * @returns {Promise<IWishListLine[]>} Promise resolving to the reloaded favorites list
 *
 * @example
 * // Remove item from favorites
 * dispatch(removeFromFavorites({ lineURL: favoriteLine.url }));
 *
 * @sideEffects
 * - Makes API call to remove wishlist line via wishlistsAPI.removeLine
 * - Dispatches loadFavorites thunk to reload the favorites list
 * - Updates `data.wishlists[LIST_FAVORITES]` with reloaded data via extraReducers
 */
export const removeFromFavorites = createAsyncThunk(
    "checkout/removeFromFavorites",
    async (
        args: RemoveFromFavoritesArgs,
        { dispatch },
    ): Promise<readonly IWishListLine[]> => {
        await wishlistsAPI.removeLine(args.lineURL);
        const result = await dispatch(loadFavorites()).unwrap();
        return result;
    },
);
